/**
 * publisher.ts — capture a webcam track, encode with WebCodecs (VP8), and ship
 * EncodedVideoChunks as binary WS frames tagged with our streamId.
 *
 * VP8 chosen for v1: keyframes are self-describing, so subscribers don't need
 * an out-of-band codec description (unlike H264's SPS/PPS). Audio is added in
 * a later step.
 */
import { encodeFrame, TRACK_VIDEO, TYPE_DELTA, TYPE_KEY } from './protocol.js';

export interface PublisherOptions {
    streamId: number;
    track: MediaStreamTrack;
    sendBinary: (data: Uint8Array) => void;
    bitrate?: number;     // default 800 kbps
    framerate?: number;   // default 30
    keyframeIntervalMs?: number; // force a keyframe at least this often (default 2000)
}

export class Publisher {
    private encoder: VideoEncoder | null = null;
    private reader: ReadableStreamDefaultReader<VideoFrame> | null = null;
    private stopped = false;
    private forceKeyNext = true;        // first frame must be a keyframe
    private lastKeyframeMs = 0;
    private readonly opts: Required<PublisherOptions>;

    constructor(options: PublisherOptions) {
        this.opts = {
            bitrate: 800_000,
            framerate: 30,
            keyframeIntervalMs: 2000,
            ...options,
        };
    }

    async start(): Promise<void> {
        const settings = this.opts.track.getSettings();
        const width = settings.width ?? 640;
        const height = settings.height ?? 480;

        this.encoder = new VideoEncoder({
            output: (chunk, meta) => this.onChunk(chunk, meta),
            error: (e) => { console.error('[vidchat] encoder error', e); },
        });
        this.encoder.configure({
            codec: 'vp8',
            width,
            height,
            bitrate: this.opts.bitrate,
            framerate: this.opts.framerate,
            latencyMode: 'realtime',
        });

        const processor = new MediaStreamTrackProcessor({ track: this.opts.track });
        this.reader = processor.readable.getReader();
        void this.pump();
    }

    /** Ask the encoder to emit a keyframe on the next frame (PLI from server). */
    requestKeyframe(): void { this.forceKeyNext = true; }

    private async pump(): Promise<void> {
        const reader = this.reader;
        const encoder = this.encoder;
        if (!reader || !encoder) return;
        try {
            while (!this.stopped) {
                const { value: frame, done } = await reader.read();
                if (done) break;
                if (!frame) continue;
                if (encoder.state !== 'configured') { frame.close(); continue; }
                // backpressure: if the encoder queue is backed up, drop this frame
                if (encoder.encodeQueueSize > 4) { frame.close(); continue; }

                const now = performance.now();
                let keyFrame = this.forceKeyNext;
                if (now - this.lastKeyframeMs >= this.opts.keyframeIntervalMs) keyFrame = true;
                if (keyFrame) { this.forceKeyNext = false; this.lastKeyframeMs = now; }

                try { encoder.encode(frame, { keyFrame }); }
                catch (e) { console.error('[vidchat] encode threw', e); }
                frame.close();
            }
        } catch (e) {
            if (!this.stopped) console.error('[vidchat] pump error', e);
        }
    }

    private onChunk(chunk: EncodedVideoChunk, _meta?: EncodedVideoChunkMetadata): void {
        const buf = new Uint8Array(chunk.byteLength);
        chunk.copyTo(buf);
        const frame = encodeFrame(
            {
                streamId: this.opts.streamId,
                track: TRACK_VIDEO,
                type: chunk.type === 'key' ? TYPE_KEY : TYPE_DELTA,
                timestamp: chunk.timestamp,
            },
            buf,
        );
        this.opts.sendBinary(frame);
    }

    stop(): void {
        this.stopped = true;
        try { this.reader?.cancel(); } catch { /* ignore */ }
        this.reader = null;
        try { if (this.encoder && this.encoder.state !== 'closed') this.encoder.close(); } catch { /* ignore */ }
        this.encoder = null;
    }
}
